import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";

import {
  Alert,
  Button,
  DesignToken,
  Display,
  Heading,
  PromoBannerV2,
  SpacingToken,
  Text,
  Tooltip,
} from "@livechat/design-system-react-components";

import { ExternalLink } from "components/external-link";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { StyledLink } from "components/links/StyledLink";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import {
  SettingsTableColumn as RawColumn,
  SettingsTableRow as Row,
  SettingsTable as Table,
} from "components/settings-table";
import { CUSTOM_FIELD_NAME } from "constants/custom-fields";
import { HelpCenter } from "constants/help-center";
import { SettingsSection } from "constants/settings/sections";
import { EventPlace, trackEngagement } from "helpers/analytics";
import useSettingsSection from "hooks/settings/use-settings-section";
import { useToggle } from "hooks/use-toggle";
import { PageWrapper } from "routes/reports/exports";
import { UpsellAlert } from "routes/subscription/UpsellAlert";
import { AgentActions, selectHasSeenCustomFieldsPromoBanner, selectIsAdmin } from "store/entities/agents";
import { customFieldActions } from "store/entities/custom-fields/actions";
import { selectCustomFieldsList } from "store/entities/custom-fields/selectors";
import { selectMaxCustomFieldsActive } from "store/entities/license";
import { selectTeams } from "store/entities/teams";
import { withVar } from "themes/helpers";
import { plural } from "utils/string";

import SettingsViewContainer from "../ViewContainer";
import { TeamsPreview } from "../components/TeamsPreview";
import { ContentWrapper } from "../styled";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { CUSTOM_FIELDS_MODAL_MODES, CustomFieldModal } from "./CustomFieldModal";
import { CustomFieldStatusButton } from "./CustomFieldStatusButton";
import { CustomFieldsEmptyState } from "./CustomFieldsEmptyState";
import { deactivateButtonStyles } from "./styles";

const Column: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => <RawColumn paddingDesktop="1em 1.5em">{children}</RawColumn>;

export const CustomFields: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const teams = useSelector(selectTeams);
  const customFields = useSelector(selectCustomFieldsList);
  const isAdmin = useSelector(selectIsAdmin);
  const maxCustomFieldsActive = useSelector(selectMaxCustomFieldsActive);
  const hasSeenCustomFieldsPromoBanner = useSelector(selectHasSeenCustomFieldsPromoBanner);

  const [modalMode, setModalMode] = useState<CUSTOM_FIELDS_MODAL_MODES>(CUSTOM_FIELDS_MODAL_MODES.CREATE);
  const [selectedCustomFieldId, setSelectedCustomFieldId] = useState<string>("");

  const [customFieldModalOpen, toggleCustomFieldModal] = useToggle(false);
  const [deactivateModalOpen, toggleDeactivateModalOpen] = useToggle(false);
  const [deleteModalOpen, toggleDeleteModalOpen] = useToggle(false);

  const activeCustomFieldsCount = customFields.filter(customField => customField.status === "active").length;
  const activeCustomFieldsLimitReached = activeCustomFieldsCount >= maxCustomFieldsActive;
  const canActivateCustomField = activeCustomFieldsCount < maxCustomFieldsActive;

  useEffect(() => {
    const hash = location.hash.slice(1);
    if (hash === "create") {
      toggleCreateModal();
      history.replace(location.pathname);
    }
  }, [location.hash]);

  useEffect(() => {
    dispatch(customFieldActions.fetchCollection());
    trackEngagement("Settings: Custom fields section opened", EventPlace.Settings);
  }, [dispatch]);

  useSettingsSection(SettingsSection.CustomFields);
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  function toggleCreateModal() {
    trackEngagement("Custom fields: Create custom field modal open", EventPlace.Settings);
    setModalMode(CUSTOM_FIELDS_MODAL_MODES.CREATE);
    setSelectedCustomFieldId("");
    toggleCustomFieldModal();
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  function toggleEditModal(id: string) {
    trackEngagement("Custom fields: Edit custom field modal open", EventPlace.Settings);
    setSelectedCustomFieldId(id);
    setModalMode(CUSTOM_FIELDS_MODAL_MODES.EDIT);
    toggleCustomFieldModal();
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  function closeCustomFieldModal() {
    toggleCustomFieldModal();
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  function toggleDeactivateModal(id: string) {
    setSelectedCustomFieldId(id);
    toggleDeactivateModalOpen();
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  function handleDeactivate(id: string) {
    dispatch(customFieldActions.update({ id, status: "deactivated" }));
    toggleDeactivateModalOpen();
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  function handleActivate(id: string) {
    dispatch(customFieldActions.update({ id, status: "active" }));
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  function toggleDeleteModal(id: string) {
    setSelectedCustomFieldId(id);
    toggleDeleteModalOpen();
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleDelete = () => {
    dispatch(customFieldActions.delete({ id: selectedCustomFieldId }));
    toggleDeleteModalOpen();
  };

  function handleClosePromoBannerClick(): void {
    dispatch(
      AgentActions.setFlag({ name: "hasSeenCustomFieldsPromoBanner_47ce93fa4cd1294738cb8d730558107f", value: true })
    );
    trackEngagement("Custom fields: Promo banner closed", EventPlace.Settings);
  }

  return (
    <SettingsViewContainer title="Custom fields">
      <PageWrapper>
        <ContentWrapper>
          {!hasSeenCustomFieldsPromoBanner && (
            <PromoBanner onClose={handleClosePromoBannerClick} css={css({ maxWidth: "900px" })}>
              <Flex column alignItems="flex-start">
                <Display>Make your tickets data-rich</Display>
                <Text>
                  Create custom fields to store additional information about tickets, requesters, and organizations in
                  the ticket details.
                  <br />
                  <br />
                  Think about their purpose and the data they&apos;ll contain. You can go with, for example, order
                  number, order details, contact ID, requester&apos;s website, delivery date, credit card number, social
                  security number, requester&apos;s age, shipping address, deal worth, and so on.
                </Text>
                <Button href={HelpCenter.CREATE_CUSTOM_FIELDS} kind="text" size="compact" target="_blank">
                  Learn more
                </Button>
              </Flex>
            </PromoBanner>
          )}
          <Block marginV={withVar(SpacingToken.Spacing6)}>
            <Heading size="xs" as="h2">
              Create custom fields
            </Heading>
            <Text css={{ marginBottom: withVar(SpacingToken.Spacing3) }}>
              Use custom fields to specify your tickets with additional information helpful for your work. Custom fields
              will be visible in the ticket details.{" "}
              <ExternalLink
                href={HelpCenter.CREATE_CUSTOM_FIELDS}
                onClick={() => trackEngagement("Custom fields: Learn more", EventPlace.Settings)}
              >
                Learn more
              </ExternalLink>
            </Text>
            <Button
              size="compact"
              kind="primary"
              onClick={toggleCreateModal}
              data-testid="create-custom-field"
              disabled={!isAdmin || activeCustomFieldsLimitReached}
            >
              Create custom field
            </Button>
            {!isAdmin && (
              <Block maxWidth="800px" marginTop="24px" marginBottom="24px">
                <Alert kind="info">
                  You can&apos;t manage custom fields in your current HelpDesk role.{" "}
                  <StyledLink
                    kind="link-light"
                    to="/agents"
                    onClick={() => trackEngagement("Custom fields: Contact any admin", EventPlace.Settings)}
                  >
                    Contact any admin
                  </StyledLink>{" "}
                  to change your role to admin.
                </Alert>
              </Block>
            )}
            {activeCustomFieldsLimitReached && (
              <Block marginV={withVar(SpacingToken.Spacing5)}>
                <UpsellAlert source="customFields" />
              </Block>
            )}
            <Block marginBottom="46px" />
            <Table
              columns={5}
              marginTop="20px"
              marginBottom="40px"
              columnSizes="1fr 1fr 1fr 1fr 1fr"
              headers={["Display name", "Team", "Type", "Uses", "Actions"]}
            >
              {customFields.map(customField => {
                const deactivated = customField.status === "deactivated";
                const ticketsCount = customField.tickets;
                return (
                  <Row key={customField.id}>
                    <Column>
                      {isAdmin ? (
                        <Button
                          kind="link-light"
                          disabled={deactivated}
                          onClick={() => toggleEditModal(customField.id)}
                          data-testid={`edit-custom-field-${customField.displayName}`}
                          css={{ textAlign: "left" }}
                        >
                          {customField.displayName}
                        </Button>
                      ) : (
                        <Text>{customField.displayName}</Text>
                      )}
                    </Column>
                    <Column>
                      <Text customColor={deactivated ? withVar(DesignToken.ContentBasicDisabled) : undefined}>
                        <TeamsPreview teams={customField.teamIDs.map(teamID => teams[teamID]).filter(Boolean)} />
                      </Text>
                    </Column>
                    <Column>
                      <Text customColor={deactivated ? withVar(DesignToken.ContentBasicDisabled) : undefined}>
                        {CUSTOM_FIELD_NAME[customField.type]}
                      </Text>
                    </Column>
                    <Column>
                      {ticketsCount ? (
                        <Button
                          kind="link-light"
                          onClick={() => {
                            history.push(
                              `/tickets/search?query=${encodeURIComponent(`customFields.${customField.apiKey}?`)}`
                            );
                            trackEngagement("Show tickets with custom field", EventPlace.Settings, {
                              customFieldId: customField.id,
                            });
                          }}
                        >
                          {`${ticketsCount} ${plural(ticketsCount, "ticket", "tickets")}`}
                        </Button>
                      ) : (
                        <Text>0 tickets</Text>
                      )}
                    </Column>
                    <Column>
                      <Flex column alignItems="flex-start">
                        {!deactivated && (
                          <Button
                            kind="link-light"
                            disabled={!isAdmin}
                            onClick={() => toggleEditModal(customField.id)}
                            data-testid={`edit-custom-field-action-${customField.displayName}`}
                          >
                            Edit
                          </Button>
                        )}
                        <CustomFieldStatusButton
                          id={customField.id}
                          isAdmin={isAdmin}
                          deactivated={deactivated}
                          canActivateCustomField={canActivateCustomField}
                          displayName={customField.displayName}
                          handleActivate={handleActivate}
                          toggleDeactivateModal={toggleDeactivateModal}
                        />
                        {!deactivated && (
                          <Button
                            kind="link-light"
                            css={deactivateButtonStyles}
                            disabled={customField.tickets > 0 || !isAdmin}
                            onClick={() => toggleDeleteModal(customField.id)}
                            className="delete-custom-field-action"
                          >
                            {customField.tickets === 0 ? (
                              "Delete"
                            ) : (
                              <Tooltip
                                placement="top"
                                triggerRenderer={() => "Delete"}
                                theme="invert"
                                css={css({ width: "246px" })}
                              >
                                <span>
                                  To delete this custom field, you must first manually clear the custom field values
                                  from all tickets.
                                </span>
                              </Tooltip>
                            )}
                          </Button>
                        )}
                      </Flex>
                    </Column>
                  </Row>
                );
              })}
            </Table>
            {!customFields.length && (
              <CustomFieldsEmptyState disableButton={!isAdmin} toggleAddModal={toggleCreateModal} />
            )}
          </Block>
        </ContentWrapper>
      </PageWrapper>
      {customFieldModalOpen && (
        <CustomFieldModal id={selectedCustomFieldId} mode={modalMode} closeModal={closeCustomFieldModal} />
      )}
      {deactivateModalOpen && (
        <ConfirmActionModal
          heading="Deactivate custom field?"
          onClose={toggleDeactivateModalOpen}
          primaryAction={{
            label: "Deactivate",
            onClick: () => handleDeactivate(selectedCustomFieldId),
            actionType: "destructive",
          }}
          secondaryAction={{
            label: "Cancel",
            onClick: toggleDeactivateModalOpen,
          }}
        >
          If you deactivate this custom field, it&apos;ll disappear from the ticket details. However, you can activate
          it at any time in the future with a complete data set.
        </ConfirmActionModal>
      )}
      {deleteModalOpen && (
        <ConfirmActionModal
          heading="Delete custom field?"
          onClose={toggleDeleteModalOpen}
          primaryAction={{
            label: "Delete",
            onClick: handleDelete,
            actionType: "destructive",
          }}
          secondaryAction={{
            label: "Cancel",
            onClick: toggleDeleteModalOpen,
          }}
        >
          If you delete this custom field, it&apos;ll disappear from the ticket details and custom field list once and
          for all.
        </ConfirmActionModal>
      )}
    </SettingsViewContainer>
  );
};

const PromoBanner = styled(PromoBannerV2)`
  background: var(${DesignToken.SurfaceGradient07});
  max-width: 900px;
`;
